import { computed, ref } from 'vue';
import type { ComputedRef, Ref } from 'vue';

import type { DateFrom } from './types';
import {
  clampFlexible,
  deriveDateFrom,
  getAnchor,
  getMaxDate,
  getMinDate,
  normalizeDateFrom,
  parseDate,
  toISO,
} from '../lib/dateFrom';
import { formatDateValue } from '../lib/formatDateLabel';

export interface UseDateFrom {
  value: Ref<DateFrom>;
  controlActivated: Ref<boolean>;

  selection: ComputedRef<DateFrom>;
  anchorIso: ComputedRef<string>;
  flexible: ComputedRef<number>;
  flexibleForCalendar: ComputedRef<number | null>;
  valueLabel: ComputedRef<string>;
  minDate: ComputedRef<string>;
  maxDate: ComputedRef<string>;

  open: () => void;
  close: () => void;
  toggle: () => void;
  setAnchorFromISO: (payload: string | { start?: string } | null | undefined) => void;
  setFlexible: (n: number) => void;
  restore: (input: DateFrom | null) => void;
  validate: () => boolean;
}

export interface UseDateFromOptions {
  initial?: DateFrom | null;
}

export function useDateFrom(options: UseDateFromOptions = {}): UseDateFrom {
  const initialValue = normalizeDateFrom(options.initial ?? null);

  const value = ref<DateFrom>(initialValue);
  const controlActivated = ref(false);

  const selection = computed<DateFrom>(() => value.value);

  const anchorIso = computed<string>(() => toISO(getAnchor(value.value)));

  const flexible = computed<number>(() => value.value.daysFlexible);

  const flexibleForCalendar = computed<number | null>(() => value.value.daysFlexible || null);

  const valueLabel = computed<string>(() =>
    formatDateValue(value.value, getAnchor(value.value)),
  );

  const minDate = computed<string>(() => toISO(getMinDate()));
  const maxDate = computed<string>(() => toISO(getMaxDate()));

  const open = (): void => {
    controlActivated.value = true;
  };

  const close = (): void => {
    controlActivated.value = false;
  };

  const toggle = (): void => {
    controlActivated.value = !controlActivated.value;
  };

  const setAnchorFromISO = (
    payload: string | { start?: string } | null | undefined,
  ): void => {
    const iso = typeof payload === 'string' ? payload : payload?.start;
    if (!iso) {
      return;
    }
    value.value = deriveDateFrom(parseDate(iso), value.value.daysFlexible);
  };

  const setFlexible = (n: number): void => {
    const next = n === value.value.daysFlexible ? 0 : clampFlexible(n);
    value.value = deriveDateFrom(getAnchor(value.value), next);
  };

  const restore = (input: DateFrom | null): void => {
    value.value = normalizeDateFrom(input);
    controlActivated.value = false;
  };

  const validate = (): boolean => !!value.value.from;

  return {
    value,
    controlActivated,
    selection,
    anchorIso,
    flexible,
    flexibleForCalendar,
    valueLabel,
    minDate,
    maxDate,
    open,
    close,
    toggle,
    setAnchorFromISO,
    setFlexible,
    restore,
    validate,
  };
}
